import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from '@mc/i18n'
import { Category } from '@mc/api'

import { usePreselectQuizExperiment } from 'experiments/pen-4674_v1-preselect-quiz'
import {
  useCategoriesStatic,
  useCategoryNameToIdMap,
  useMotivations,
  useMotivationToCategoriesMap,
  useMotivationsCopyMap,
  useSelectedCategories,
  useOtherCategories,
} from 'pages/find-my-classes/hooks/useCategorySelections'

import { ctaPath } from '../../ctaPath'
import { Sel } from './WhatBringsYouVariantPALM'

export const useWhatBringsYouVariantPALM = (
  preselectedCategories?: Category[],
) => {
  const { t } = useTranslation('@mc/next')
  const categories = useCategoriesStatic()
  const [sel, setSel] = useState<Sel>(new Map())

  const { isPreselectQuizVariant } = usePreselectQuizExperiment()
  const { selectedCategories, toggle, isSelected } = useSelectedCategories({
    forcedCategories: preselectedCategories?.map((cat) => cat.id),
    includeInterests: isPreselectQuizVariant,
  })
  const selectedCategoryIds = useMemo(
    () => [...selectedCategories.values()],
    [selectedCategories],
  )

  const ctaHref = ctaPath({
    skipQ1: true,
    categories: selectedCategoryIds,
  })

  const otherCategories = useOtherCategories()
  const otherIsPreselected = useMemo(
    () =>
      !!preselectedCategories?.some((cat) =>
        otherCategories.includes(
          cat.name as 'Games & Digital Media' | 'Outdoor Adventures & Events',
        ),
      ),
    [otherCategories, preselectedCategories],
  )
  const catNameToId = useCategoryNameToIdMap()
  const motivationToCatName = useMotivationToCategoriesMap(otherIsPreselected)
  const motivations = useMotivations(otherIsPreselected)
  const motivationCopyMap = useMotivationsCopyMap(motivations, t)
  const otherCategoryIds = useMemo(
    () =>
      motivationToCatName.other?.map((otherCatName) =>
        catNameToId.get(otherCatName),
      ) || [],
    [catNameToId, motivationToCatName.other],
  )

  const trackedCategoryIds = useMemo(
    () =>
      selectedCategoryIds.filter(
        (categoryId) => !otherCategoryIds.includes(categoryId),
      ),
    [otherCategoryIds, selectedCategoryIds],
  )

  const trackedCategoryNames = useMemo(
    () =>
      trackedCategoryIds.map((categoryId) => {
        const category = categories?.find((c) => Number(c.id) === categoryId)
        return category?.name ?? '<null category>' // null should never happen
      }),
    [categories, trackedCategoryIds],
  )

  const selectedOptions = useMemo(
    () =>
      [...sel.entries()]
        .filter(([_key, val]) => !!val)
        .map(([motivation]) => motivationCopyMap[motivation]),
    [motivationCopyMap, sel],
  )

  // pen-4558_v1 and pen-4674_v1: Preselect motivations based on preselected categories
  useEffect(() => {
    const preselectedMotivations = new Map() as Sel

    if (selectedCategories.size > 0) {
      Object.entries(motivationToCatName).reduce(
        (acc, [motivation, categoryNames]) => {
          const hasPreselectedCategory = categoryNames?.some((name) =>
            selectedCategories.has(name),
          )

          if (hasPreselectedCategory) {
            preselectedMotivations.set(motivation, true)
          }

          return acc
        },
        preselectedMotivations,
      )
    }

    setSel(preselectedMotivations)
  }, [motivationToCatName, selectedCategories])

  // Only pen-4558_v1: Select all categories from preselected motivations
  useEffect(() => {
    if (selectedCategories.size > 0 && !isPreselectQuizVariant) {
      Object.values(motivationToCatName).forEach((categoryNames) => {
        const hasPreselectedCategory = categoryNames?.some((name) =>
          selectedCategories.has(name),
        )
        if (hasPreselectedCategory) {
          categoryNames?.forEach((name) => {
            if (!selectedCategories.has(name)) {
              toggle(catNameToId.get(name) as number)
            }
          })
        }
      })
    }
  }, [
    catNameToId,
    motivationToCatName,
    toggle,
    selectedCategories,
    isPreselectQuizVariant,
  ])

  return {
    sel,
    setSel,
    toggle,
    isSelected,
    selectedOptions,
    ctaHref,
    trackedCategoryNames,
    trackedCategoryIds,
    otherIsPreselected,
  }
}
